import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { log } from "../lib/log";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "cardscan_jwt";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(() => {
    try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
  });
  const [user, setUser] = useState(null);   // null = unknown, false = no auth, object = signed in
  const [loading, setLoading] = useState(true);

  const setToken = useCallback((t) => {
    setTokenState(t || "");
    try {
      if (t) localStorage.setItem(TOKEN_KEY, t);
      else localStorage.removeItem(TOKEN_KEY);
    } catch (e) {
      // localStorage can throw in Safari private mode or when storage is full
      log.warn("[auth] localStorage unavailable:", e?.message);
    }
  }, []);

  // Bootstrap: if we have a token, fetch /auth/me to confirm
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      if (!token) { setUser(false); setLoading(false); return; }
      try {
        const r = await fetch(`${BACKEND_URL}/api/auth/me`, {
          headers: { "Authorization": `Bearer ${token}` },
        });
        if (cancelled) return;
        if (r.ok) {
          const d = await r.json();
          setUser(d.user);
        } else {
          setToken("");
          setUser(false);
        }
      } catch {
        if (!cancelled) { setUser(false); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    bootstrap();
    return () => { cancelled = true; };
  }, [token, setToken]);

  const login = useCallback(async (email, password, deviceId) => {
    const r = await fetch(`${BACKEND_URL}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, device_id: deviceId || null }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      const detail = d.detail || d.message || "Login failed";
      const msg = typeof detail === "string" ? detail :
        Array.isArray(detail) ? detail.map((e) => e?.msg || JSON.stringify(e)).join(" ") :
        JSON.stringify(detail);
      throw new Error(msg);
    }
    if (d.requires_2fa) {
      // Caller handles the 2FA challenge UI; don't set token/user yet.
      return { requires_2fa: true, challenge_id: d.challenge_id, email_hint: d.email_hint };
    }
    setToken(d.access_token);
    setUser(d.user);
    return d.user;
  }, [setToken]);

  /** Complete a 2FA challenge with the 6-digit code. */
  const verify2FA = useCallback(async ({ challenge_id, code, device_id, trust_device }) => {
    const r = await fetch(`${BACKEND_URL}/api/auth/2fa/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ challenge_id, code, device_id, trust_device }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      const detail = d.detail || "2FA verification failed";
      throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
    }
    setToken(d.access_token);
    setUser(d.user);
    return d.user;
  }, [setToken]);

  /** Re-send a fresh 6-digit code; returns the new challenge_id. */
  const resend2FA = useCallback(async (challenge_id) => {
    const r = await fetch(`${BACKEND_URL}/api/auth/2fa/resend`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ challenge_id }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.detail || "Could not re-send code");
    return d;  // { ok, challenge_id, email_hint }
  }, []);

  const signup = useCallback(async (email, password, name, referralCode, opts = {}) => {
    const r = await fetch(`${BACKEND_URL}/api/auth/signup`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email, password,
        name: name || "",
        referral_code: referralCode || null,
        accepted_terms: !!opts.acceptedTerms,
      }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      const detail = d.detail || d.message || "Signup failed";
      const msg = typeof detail === "string" ? detail :
        Array.isArray(detail) ? detail.map((e) => e?.msg || JSON.stringify(e)).join(" ") :
        JSON.stringify(detail);
      throw new Error(msg);
    }
    setToken(d.access_token);
    setUser(d.user);
    return d.user;
  }, [setToken]);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setUser(d.user);
      }
    } catch (e) {
      log.warn("[auth] refreshUser failed:", e?.message);
    }
  }, [token]);

  const logout = useCallback(() => {
    setToken("");
    setUser(false);
  }, [setToken]);

  /** Authenticated fetch — adds the Bearer header automatically.
   *  Handles 401 by signing out. Returns the parsed JSON or throws Error. */
  const apiFetch = useCallback(async (path, opts = {}) => {
    const headers = new Headers(opts.headers || {});
    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (opts.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
    const r = await fetch(`${BACKEND_URL}${path}`, { ...opts, headers });
    if (r.status === 401) {
      setToken("");
      setUser(false);
    }
    const ct = r.headers.get("content-type") || "";
    const data = ct.includes("application/json") ? await r.json().catch(() => ({})) : await r.text();
    if (!r.ok) {
      const detail = (data && typeof data === "object" ? data.detail : null) || `HTTP ${r.status}`;
      const msg = typeof detail === "string" ? detail :
        Array.isArray(detail) ? detail.map((e) => e?.msg || JSON.stringify(e)).join(" ") :
        JSON.stringify(detail);
      const err = new Error(msg);
      err.status = r.status;
      throw err;
    }
    return data;
  }, [token, setToken]);

  const value = { user, token, loading, login, verify2FA, resend2FA, signup, logout, apiFetch, refreshUser };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
